import { paths } from "@/app/paths"
import { slugify } from "@/utils/slugify"

export type RealEstateAgentHrefInput = {
  displayName: string
  uniqueListingIdHash: string
  /** Prefer office location when the API sends it (matches roster / `generateAgentLink`). */
  officeCity?: string | null
  officeStateOrProvince?: string | null
  /** Fallback for the `/real-estate-agents/{locationSlug}/…` segment when office geo is absent. */
  listingCity?: string | null
  listingStateOrProvince?: string | null
}

/**
 * Builds `/real-estate-agents/{locationSlug}/{nameSlug}-{UniqueListingIdHash}`.
 * `uniqueListingIdHash` must be the broker **UniqueListingIdHash** (same as roster /
 * {@link generateAgentLink}), not RESO `ListAgentKey` / MLS ids.
 *
 * The bio page loads the member via `GET /v1/member/{hash}` using the **last** `-` segment
 * of the URL id param.
 *
 * @see paths.realEstateAgent
 * @see src/app/real-estate-agents/[locationSlug]/[id]/page.tsx
 * @see `.cursor/docs/agent-office-search.md` (Agent schema — primary key for links)
 */
export function realEstateAgentHref(
  input: RealEstateAgentHrefInput
): string | null {
  const hash = input.uniqueListingIdHash?.trim()
  const displayName = input.displayName?.trim()
  if (!hash || !displayName) return null

  const city =
    input.officeCity?.trim() || input.listingCity?.trim() || ""
  const state =
    input.officeStateOrProvince?.trim() ||
    input.listingStateOrProvince?.trim() ||
    ""

  const locationSlug =
    city && state ? `${slugify(city)}-${slugify(state)}` : "unknown"
  const agentSlug = `${slugify(displayName)}-${hash}`
  return paths.realEstateAgent(locationSlug, agentSlug)
}
